// Usuarios.tsx

import React, { useState, useEffect } from 'react';
import { fetchUsuarios, createUsuario, updateUsuario, deleteUsuario } from '../api/userApi';
import '../css/usuarios.css';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import LoadingLottie from '../components/LoadingLottie';

function determinarRol(usuario: any) {
  if (
    usuario.tecnico &&
    !usuario.comercial &&
    !usuario.cliente_admin &&
    usuario.id_cliente === null &&
    usuario.activo
  ) {
    return 'tecnico';
  } else if (
    !usuario.tecnico &&
    !usuario.comercial &&
    usuario.cliente_admin &&
    usuario.id_cliente !== 0 &&
    usuario.activo
  ) {
    return 'cliente';
  } else if (
    !usuario.tecnico &&
    !usuario.comercial &&
    !usuario.cliente_admin &&
    usuario.id_cliente === null &&
    usuario.activo
  ) {
    return 'admin';
  } else {
    return 'sin rol';
  }
}

function applyRoleChanges(role: string, editingUserData: any) {
  const updated = { ...editingUserData };

  if (updated.id_usuario_padre) {
    // Si es subusuario, ignoramos el cambio de rol
    return updated;
  }

  updated.tecnico = false;
  updated.comercial = false;
  updated.cliente_admin = false;
  updated.id_cliente = null;

  if (role === 'admin') {
    // Admin sin cliente ni tecnico
  } else if (role === 'tecnico') {
    updated.tecnico = true;
  } else if (role === 'cliente') {
    updated.cliente_admin = true;
    updated.id_cliente = 999;
  }

  return updated;
}

interface ParentWithSubusers {
  parent: any;
  subusers: any[];
}

interface FetchUsuariosResponse {
  data: ParentWithSubusers[]; // Ahora el backend devuelve esta estructura
  total: number;
  page: number;
  limit: number;
}

const Usuarios: React.FC = () => {
  const [usuariosData, setUsuariosData] = useState<ParentWithSubusers[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const searchTerm = searchParams.get('search') || '';
  const activo = searchParams.get('activo') || '';
  const ordenarPor = searchParams.get('ordenarPor') || '';
  const rolFiltro = searchParams.get('rol') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);

  // Estados para filtros pendientes (para el campo de búsqueda, dropdowns, etc.)
  const [pendingSearchTerm, setPendingSearchTerm] = useState(searchTerm);
  const [pendingActivo, setPendingActivo] = useState(activo);
  const [pendingOrdenarPor, setPendingOrdenarPor] = useState(ordenarPor);
  const [pendingRol, setPendingRol] = useState(rolFiltro);

  const [editingUserId, setEditingUserId] = useState<string | 'new' | null>(null);
  const [editingUserData, setEditingUserData] = useState<any>({});
  const [selectedRole, setSelectedRole] = useState<string>('admin');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [creatingUser, setCreatingUser] = useState(false);

  const [creatingSubUserParentId, setCreatingSubUserParentId] = useState<string | null>(null);
  const [editingSubUserId, setEditingSubUserId] = useState<string | null>(null);
  const [editingSubUserData, setEditingSubUserData] = useState<any>({});
  const [showSubUsers, setShowSubUsers] = useState<{ [key: string]: boolean }>({});

  // Definir la URL base del backend
  const backendUrl = process.env.REACT_APP_BACKEND_URL || `${window.location.protocol}//${window.location.hostname}`;

  const loadUsuarios = async () => {
    setLoading(true);
    setError(null);
    try {
      const response: FetchUsuariosResponse = await fetchUsuarios({
        search: searchTerm || undefined,
        activo: activo || undefined,
        ordenarPor: ordenarPor || undefined,
        rol: rolFiltro || undefined,
        page: page,
        limit: 10
      });
      setUsuariosData(response.data);
      setTotalPages(Math.ceil(response.total / response.limit));

      // **Nuevo:** Asegurarse de que la página actual no exceda el total de páginas
      if (page > Math.ceil(response.total / response.limit) && response.total > 0) {
          setSearchParams({ ...Object.fromEntries(searchParams), page: '1' });
      }
    } catch (err) {
      if (err instanceof Error) {
          setError(err.message);
      } else {
          setError('Error desconocido al cargar los usuarios.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const applyFilters = () => {
    const params: Record<string, string> = {};

    if (pendingSearchTerm) params['search'] = pendingSearchTerm;
    if (pendingActivo) params['activo'] = pendingActivo;
    if (pendingOrdenarPor) params['ordenarPor'] = pendingOrdenarPor;
    if (pendingRol) params['rol'] = pendingRol;

    params['page'] = '1';
    setSearchParams(params);
  };

  const applyOrdenarPor = () => {
    const currentParams = Object.fromEntries(searchParams.entries());
    currentParams['ordenarPor'] = pendingOrdenarPor;
    currentParams['page'] = '1';
    setSearchParams(currentParams);
  };

  const handleFilterReset = () => {
    setPendingSearchTerm('');
    setPendingActivo('');
    setPendingOrdenarPor('');
    setPendingRol('');
    setSearchParams({});
  };

  const handleAddUser = () => {
    if (creatingUser) return;
    const newUser = {
      id_usuario: 'new',
      nombre: '',
      email: '',
      login: '',
      password: '',
      comercial: false,
      tecnico: false,
      cliente_admin: false,
      id_cliente: null,
      activo: true,
      emailnotificaciones: '',
      recibircorreos: false,
      profilepic: '',
      id_usuario_padre: null
    };
    setUsuariosData([{ parent: newUser, subusers: [] }, ...usuariosData]);
    setEditingUserId('new');
    const updated = applyRoleChanges('admin', newUser);
    setEditingUserData({ ...updated, id_usuario: 'new' });
    setSelectedRole('admin');
    setShowPassword(false);
    setCreatingUser(true);
  };

  const handleCancelCreate = () => {
    setUsuariosData((prev) => prev.filter(u => u.parent.id_usuario !== 'new'));
    setEditingUserId(null);
    setEditingUserData({});
    setCreatingUser(false);
    setSelectedRole('admin');
  };

  const handleEdit = (user: any) => {
    if (creatingUser && editingUserId === 'new') return;
    setEditingUserId(user.id_usuario);
    setEditingUserData({ ...user });
    const rolActual = determinarRol(user);
    setSelectedRole(rolActual || 'admin');
    setShowPassword(false);
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditingUserData({});
    setSelectedRole('admin');
    setCreatingUser(false);
  };

  const handleSave = async (userId: string | 'new') => {
    try {
      const { hash, ...dataToUse } = editingUserData;
      const finalData = applyRoleChanges(selectedRole, { ...dataToUse });
      const sendData = {
        ...finalData,
        activo: finalData.activo !== undefined ? finalData.activo : true,
      };

      if (userId === 'new') {
        await createUsuario(sendData);
      } else {
        await updateUsuario(userId, sendData);
      }

      setEditingUserId(null);
      setEditingUserData({});
      setSelectedRole('admin');
      setShowPassword(false);
      setCreatingUser(false);
      loadUsuarios();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Error desconocido al guardar el usuario.');
      }
    }
  };

  const handleDelete = async (userId: string) => {
    if (creatingUser && userId === 'new') {
      handleCancelCreate();
      return;
    }

    if (!window.confirm('¿Está seguro de eliminar este usuario?')) return;
    try {
      await deleteUsuario(userId);
      loadUsuarios();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Error desconocido al eliminar el usuario.');
      }
    }
  };

  const handleChangeField = (field: string, value: any) => {
    setEditingUserData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleRoleChange = (newRole: string) => {
    if (editingUserData.id_usuario_padre) return;
    setSelectedRole(newRole);
    const updated = applyRoleChanges(newRole, editingUserData);
    setEditingUserData(updated);
  };

  const handleAddSubUserClick = (parentId: string) => {
    if (parentId === 'new') {
      alert('Guarde el usuario principal antes de añadir subusuarios.');
      return;
    }
    setCreatingSubUserParentId(parentId);
    setEditingSubUserId('new_subuser');
    setEditingSubUserData({
      id_usuario: 'new_subuser',
      nombre: '',
      email: '',
      login: '',
      password: '',
      comercial: false,
      tecnico: false,
      cliente_admin: false,
      id_cliente: null,
      activo: true,
      emailnotificaciones: '',
      recibircorreos: false,
      profilepic: '',
      id_usuario_padre: parentId
    });
  };

  const handleCancelSubUserCreateOrEdit = () => {
    setCreatingSubUserParentId(null);
    setEditingSubUserId(null);
    setEditingSubUserData({});
  };

  const handleSaveSubUser = async () => {
    try {
      const { hash, ...dataToUse } = editingSubUserData;
      const sendData = {
        ...dataToUse,
        activo: dataToUse.activo !== undefined ? dataToUse.activo : true,
      };

      if (editingSubUserId === 'new_subuser') {
        await createUsuario(sendData);
      } else {
        await updateUsuario(editingSubUserId as string, sendData);
      }

      setCreatingSubUserParentId(null);
      setEditingSubUserId(null);
      setEditingSubUserData({});
      loadUsuarios();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Error desconocido al guardar el subusuario.');
      }
    }
  };

  const handleEditSubUser = (subuser: any) => {
    setEditingSubUserId(subuser.id_usuario);
    setEditingSubUserData({ ...subuser });
    setShowPassword(false);
  };

  const handleDeleteSubUser = async (subUserId: string) => {
    if (!window.confirm('¿Está seguro de eliminar este subusuario?')) return;
    try {
      await deleteUsuario(subUserId);
      loadUsuarios();
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Error desconocido al eliminar el subusuario.');
      }
    }
  };

  const handleChangeSubUserField = (field: string, value: any) => {
    setEditingSubUserData((prev: any) => ({ ...prev, [field]: value }));
  };

  const toggleSubUsers = (parentId: string) => {
    setShowSubUsers((prev) => ({ ...prev, [parentId]: !prev[parentId] }));
  };

  return (
    <div className="user-dashboard">
      <div className="sort-section">
        <label htmlFor="ordenarPor">Ordenar por:</label>
        <select
          id="ordenarPor"
          value={pendingOrdenarPor}
          onChange={(e) => setPendingOrdenarPor(e.target.value)}
          className="form-control"
        >
          <option value="">Seleccione...</option>
          <option value="nombre ASC">Nombre (A - Z)</option>
          <option value="nombre DESC">Nombre (Z - A)</option>
          <option value="email ASC">Email (A - Z)</option>
          <option value="email DESC">Email (Z - A)</option>
        </select>
        <button onClick={applyOrdenarPor} className="btn btn-corporate-red">
          Aplicar
        </button>
      </div>

      <div className="filter-section">
        <input
          type="text"
          placeholder="Buscar por nombre, empresa o email..."
          value={pendingSearchTerm}
          onChange={(e) => setPendingSearchTerm(e.target.value)}
          className="form-control search-input"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyFilters()
            };
          }}
        />

        <select
          value={pendingActivo}
          onChange={(e) => setPendingActivo(e.target.value)}
          className="form-control"
        >
          <option value="">Activo/Inactivo</option>
          <option value="true">Activo</option>
          <option value="false">Inactivo</option>
        </select>

        <select
          value={pendingRol}
          onChange={(e) => setPendingRol(e.target.value)}
          className="form-control"
        >
          <option value="">Todos los roles</option>
          <option value="admin">Admin</option>
          <option value="tecnico">Técnico</option>
          <option value="cliente">Cliente</option>
        </select>

        <button onClick={applyFilters} className="btn btn-corporate-red">
          Filtrar
        </button>
        <button onClick={handleFilterReset} className="btn btn-secondary">
          Eliminar filtros
        </button>

        <button
          onClick={handleAddUser}
          className="btn btn-corporate-red add-user-btn"
          disabled={creatingUser}
        >
          Añadir usuario
        </button>
      </div>

      {loading ? (
        <LoadingLottie />
      ) : error ? (
        <p className="error-text">Error: {error}</p>
      ) : usuariosData.length === 0 ? (
        <p className="no-results-text">
          No se encontraron usuarios que coincidan con los criterios de
          búsqueda.
        </p>
      ) : (
        <div className="user-list">
          {usuariosData.map(({ parent: usuario, subusers }) => {
            const editing = editingUserId === usuario.id_usuario;
            const rol = determinarRol(usuario);
            // Construir la URL completa de la imagen de perfil
            const profilePicUrl = usuario.profilepic
              ? `${backendUrl}${usuario.profilepic}`
              : "/images/default-user.png";

            return (
              <div className="user-card" key={usuario.id_usuario}>
                <div className="usuarios-info">
                  <div className="profile-pic">
                    <img src={profilePicUrl} alt="Profile" />
                  </div>
                  <div className="user-details">
                    <div className="user-header">
                      <div className="user-name">
                        Nombre: {usuario.nombre || "N/A"}
                      </div>
                      <div
                        className={`status-tag status-${
                          usuario.activo ? "active" : "inactive"
                        }`}
                      >
                        {usuario.activo ? "Activo" : "Inactivo"}
                      </div>
                    </div>

                    <div className="user-meta">
                      <div className="meta-item">
                        <strong>Email:</strong> {usuario.email || "N/A"}
                      </div>
                      <div className="meta-item">
                        <strong>Login:</strong> {usuario.login || "N/A"}
                      </div>
                      <div className="meta-item">
                        <strong>Empresa:</strong> {usuario.empresa || "N/A"}{" "}
                      </div>
                      <div className="meta-item">
                        <strong>Rol:</strong>{" "}
                        {usuario.activo ? rol || "N/A" : "No activo"}
                      </div>
                      <div className="meta-item">
                        <strong>ID:</strong> {usuario.id_usuario || "N/A"}
                      </div>
                      <div className="meta-item">
                        <strong>Activo:</strong> {usuario.activo ? "Sí" : "No"}
                      </div>
                      <div className="meta-item">
                        <strong>Email Notificaciones:</strong>{" "}
                        {usuario.emailnotificaciones || "N/A"}
                      </div>
                      <div className="meta-item">
                        <strong>Recibir Correos:</strong>{" "}
                        {usuario.recibircorreos ? "Sí" : "No"}
                      </div>
                    </div>

                    {editing && (
                      <div className="edit-section">
                        <div className="edit-fields">
                          <div className="edit-field">
                            <label>Nombre:</label>
                            <input
                              type="text"
                              value={editingUserData.nombre || ""}
                              onChange={(e) =>
                                handleChangeField("nombre", e.target.value)
                              }
                            />
                          </div>
                          <div className="edit-field">
                            <label>Email:</label>
                            <input
                              type="email"
                              value={editingUserData.email || ""}
                              onChange={(e) =>
                                handleChangeField("email", e.target.value)
                              }
                            />
                          </div>
                          <div className="edit-field">
                            <label>Login:</label>
                            <input
                              type="text"
                              value={editingUserData.login || ""}
                              onChange={(e) =>
                                handleChangeField("login", e.target.value)
                              }
                            />
                          </div>
                          <div className="edit-field">
                            <label>Contraseña:</label>
                            <div className="password-input-wrapper">
                              <input
                                type={showPassword ? "text" : "password"}
                                value={editingUserData.password || ""}
                                onChange={(e) =>
                                  handleChangeField("password", e.target.value)
                                }
                              />
                              <button
                                type="button"
                                className="toggle-password-visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={
                                  showPassword
                                    ? "Ocultar contraseña"
                                    : "Mostrar contraseña"
                                }
                              >
                                {showPassword ? (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="#555"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 
    11-7-4-7-11-7zm0 12c-2.761 
    0-5-2.239-5-5s2.239-5 
    5-5 5 2.239 5 5-2.239 5-5 
    5zm0-8c-1.654 
    0-3 1.346-3 3s1.346 3 
    3 3 3-1.346 3-3-1.346-3-3-3z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="#555"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 
    11-7-4-7-11-7zm0 12c-2.761 
    0-5-2.239-5-5s2.239-5 
    5-5 5 2.239 5 5-2.239 5-5 
    5zm0-8c-1.654 
    0-3 1.346-3 3s1.346 3 
    3 3 3-1.346 3-3-1.346-3-3-3z"
                                    />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </div>

                          {!editingUserData.id_usuario_padre && (
                            <div className="edit-field role-selection">
                              <label>Rol:</label>
                              <div className="role-options">
                                <label>
                                  <input
                                    type="radio"
                                    name={`rol_${usuario.id_usuario}`}
                                    value="admin"
                                    checked={selectedRole === "admin"}
                                    onChange={() => handleRoleChange("admin")}
                                  />
                                  Admin
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`rol_${usuario.id_usuario}`}
                                    value="tecnico"
                                    checked={selectedRole === "tecnico"}
                                    onChange={() => handleRoleChange("tecnico")}
                                  />
                                  Técnico
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name={`rol_${usuario.id_usuario}`}
                                    value="cliente"
                                    checked={selectedRole === "cliente"}
                                    onChange={() => handleRoleChange("cliente")}
                                  />
                                  Cliente
                                </label>
                              </div>
                            </div>
                          )}

                          <div className="edit-field">
                            <label>Activo:</label>
                            <input
                              type="checkbox"
                              checked={!!editingUserData.activo}
                              onChange={(e) =>
                                handleChangeField("activo", e.target.checked)
                              }
                            />
                          </div>

                          <div className="edit-field">
                            <label>Recibir Correos:</label>
                            <input
                              type="checkbox"
                              checked={!!editingUserData.recibircorreos}
                              onChange={(e) =>
                                handleChangeField(
                                  "recibircorreos",
                                  e.target.checked
                                )
                              }
                            />
                          </div>

                          <div className="edit-field">
                            <label>Email Notificaciones:</label>
                            <input
                              type="text"
                              value={editingUserData.emailnotificaciones || ""}
                              onChange={(e) =>
                                handleChangeField(
                                  "emailnotificaciones",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="user-footer">
                  <div className="user-actions">
                    {editing ? (
                      <>
                        <button
                          onClick={() => handleSave(usuario.id_usuario)}
                          className="btn btn-primary"
                        >
                          Guardar
                        </button>
                        {usuario.id_usuario === "new" ? (
                          <button
                            onClick={handleCancelCreate}
                            className="btn btn-secondary"
                          >
                            Cancelar
                          </button>
                        ) : (
                          <button
                            onClick={handleCancelEdit}
                            className="btn btn-secondary"
                          >
                            Cancelar
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleEdit(usuario);
                          }}
                          title="Editar"
                        >
                          <FontAwesomeIcon
                            icon={faEdit}
                            className="text-primary"
                          />
                        </Link>
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleDelete(usuario.id_usuario);
                          }}
                          title="Borrar"
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="text-danger"
                          />
                        </Link>
                      </>
                    )}
                  </div>
                </div>

                <div className="subusers-section">
                  {usuario.id_usuario === "new" ? (
                    <p>
                      Guarde el usuario principal antes de añadir subusuarios.
                    </p>
                  ) : (
                    <button
                      onClick={() => handleAddSubUserClick(usuario.id_usuario)}
                      className="btn btn-corporate-red add-subuser-btn"
                      disabled={
                        creatingSubUserParentId === usuario.id_usuario &&
                        editingSubUserId === "new_subuser"
                      }
                    >
                      Añadir subusuario
                    </button>
                  )}

                  {subusers.length > 0 && (
                    <div className="subuser-toggle">
                      <button
                        onClick={() => toggleSubUsers(usuario.id_usuario)}
                        className="btn btn-light"
                      >
                        {showSubUsers[usuario.id_usuario]
                          ? "Ocultar subusuarios"
                          : "Ver subusuarios"}{" "}
                        <FontAwesomeIcon
                          icon={
                            showSubUsers[usuario.id_usuario]
                              ? faChevronUp
                              : faChevronDown
                          }
                        />
                      </button>
                    </div>
                  )}

                  {subusers.length === 0 && usuario.id_usuario !== "new" && (
                    <p>No hay subusuarios.</p>
                  )}

                  {showSubUsers[usuario.id_usuario] && subusers.length > 0 && (
                    <div className="subusers-list">
                      {subusers.map((subu) => {
                        const subEditing = editingSubUserId === subu.id_usuario;

                        return (
                          <div className="subuser-card" key={subu.id_usuario}>
                            {!subEditing && (
                              <div className="subuser-info">
                                <div>
                                  <strong>Nombre:</strong>{" "}
                                  {subu.nombre || "N/A"} -{" "}
                                  <strong>Email:</strong> {subu.email || "N/A"}
                                </div>
                                <div>
                                  <strong>Login:</strong> {subu.login || "N/A"}{" "}
                                  - <strong>Activo:</strong>{" "}
                                  {subu.activo ? "Sí" : "No"}
                                </div>
                                <div className="subuser-actions">
                                  <Link
                                    to="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleEditSubUser(subu);
                                    }}
                                    title="Editar subusuario"
                                  >
                                    <FontAwesomeIcon
                                      icon={faEdit}
                                      className="text-primary"
                                    />
                                  </Link>
                                  <Link
                                    to="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleDeleteSubUser(subu.id_usuario);
                                    }}
                                    title="Borrar subusuario"
                                  >
                                    <FontAwesomeIcon
                                      icon={faTrash}
                                      className="text-danger"
                                    />
                                  </Link>
                                </div>
                              </div>
                            )}
                            {subEditing && (
                              <div className="edit-section">
                                <div className="edit-fields">
                                  <div className="edit-field">
                                    <label>Nombre:</label>
                                    <input
                                      type="text"
                                      value={editingSubUserData.nombre || ""}
                                      onChange={(e) =>
                                        handleChangeSubUserField(
                                          "nombre",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="edit-field">
                                    <label>Email:</label>
                                    <input
                                      type="email"
                                      value={editingSubUserData.email || ""}
                                      onChange={(e) =>
                                        handleChangeSubUserField(
                                          "email",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="edit-field">
                                    <label>Login:</label>
                                    <input
                                      type="text"
                                      value={editingSubUserData.login || ""}
                                      onChange={(e) =>
                                        handleChangeSubUserField(
                                          "login",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="edit-field">
                                    <label>Contraseña:</label>
                                    <div className="password-input-wrapper">
                                      <input
                                        type={
                                          showPassword ? "text" : "password"
                                        }
                                        value={
                                          editingSubUserData.password || ""
                                        }
                                        onChange={(e) =>
                                          handleChangeSubUserField(
                                            "password",
                                            e.target.value
                                          )
                                        }
                                      />
                                      <button
                                        type="button"
                                        className="toggle-password-visibility"
                                        onClick={() =>
                                          setShowPassword(!showPassword)
                                        }
                                        aria-label={
                                          showPassword
                                            ? "Ocultar contraseña"
                                            : "Mostrar contraseña"
                                        }
                                      >
                                        {showPassword ? (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            fill="#555"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 
    11-7-4-7-11-7zm0 12c-2.761 
    0-5-2.239-5-5s2.239-5 
    5-5 5 2.239 5 5-2.239 5-5 
    5zm0-8c-1.654 
    0-3 1.346-3 3s1.346 3 
    3 3 3-1.346 3-3-1.346-3-3-3z"
                                            />
                                          </svg>
                                        ) : (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            fill="#555"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              d="M12 5c-7 0-11 
    7-11 7s4 7 11 7 11-7 
    11-7-4-7-11-7zm0 12c-2.761 
    0-5-2.239-5-5s2.239-5 
    5-5 5 2.239 5 5-2.239 5-5 
    5zm0-8c-1.654 
    0-3 1.346-3 3s1.346 3 
    3 3 3-1.346 3-3-1.346-3-3-3z"
                                            />
                                          </svg>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="edit-field">
                                    <label>Activo:</label>
                                    <input
                                      type="checkbox"
                                      checked={!!editingSubUserData.activo}
                                      onChange={(e) =>
                                        handleChangeSubUserField(
                                          "activo",
                                          e.target.checked
                                        )
                                      }
                                    />
                                  </div>

                                  <div className="edit-field">
                                    <label>Recibir Correos:</label>
                                    <input
                                      type="checkbox"
                                      checked={
                                        !!editingSubUserData.recibircorreos
                                      }
                                      onChange={(e) =>
                                        handleChangeSubUserField(
                                          "recibircorreos",
                                          e.target.checked
                                        )
                                      }
                                    />
                                  </div>

                                  <div className="edit-field">
                                    <label>Email Notificaciones:</label>
                                    <input
                                      type="text"
                                      value={
                                        editingSubUserData.emailnotificaciones ||
                                        ""
                                      }
                                      onChange={(e) =>
                                        handleChangeSubUserField(
                                          "emailnotificaciones",
                                          e.target.value
                                        )
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="user-actions">
                                  <button
                                    onClick={handleSaveSubUser}
                                    className="btn btn-primary"
                                  >
                                    Guardar
                                  </button>
                                  <button
                                    onClick={handleCancelSubUserCreateOrEdit}
                                    className="btn btn-secondary"
                                  >
                                    Cancelar
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {creatingSubUserParentId === usuario.id_usuario &&
                    editingSubUserId === "new_subuser" && (
                      <div className="edit-section">
                        <div className="edit-fields">
                          <div className="edit-field">
                            <label>Nombre:</label>
                            <input
                              type="text"
                              value={editingSubUserData.nombre || ""}
                              onChange={(e) =>
                                handleChangeSubUserField(
                                  "nombre",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <div className="edit-field">
                            <label>Email:</label>
                            <input
                              type="email"
                              value={editingSubUserData.email || ""}
                              onChange={(e) =>
                                handleChangeSubUserField(
                                  "email",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <div className="edit-field">
                            <label>Login:</label>
                            <input
                              type="text"
                              value={editingSubUserData.login || ""}
                              onChange={(e) =>
                                handleChangeSubUserField(
                                  "login",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                          <div className="edit-field">
                            <label>Contraseña:</label>
                            <div className="password-input-wrapper">
                              <input
                                type={showPassword ? "text" : "password"}
                                value={editingSubUserData.password || ""}
                                onChange={(e) =>
                                  handleChangeSubUserField(
                                    "password",
                                    e.target.value
                                  )
                                }
                              />
                              <button
                                type="button"
                                className="toggle-password-visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={
                                  showPassword
                                    ? "Ocultar contraseña"
                                    : "Mostrar contraseña"
                                }
                              >
                                {showPassword ? (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="#555"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      d="M12 5c-7 0-11 
      7-11 7s4 7 11 7 11-7 
      11-7-4-7-11-7zm0 12c-2.761 
      0-5-2.239-5-5s2.239-5 
      5-5 5 2.239 5 5-2.239 5-5 
      5zm0-8c-1.654 
      0-3 1.346-3 3s1.346 3 
      3 3 3-1.346 3-3-1.346-3-3-3z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    fill="#555"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      d="M12 5c-7 0-11 
      7-11 7s4 7 11 7 11-7 
      11-7-4-7-11-7zm0 12c-2.761 
      0-5-2.239-5-5s2.239-5 
      5-5 5 2.239 5 5-2.239 5-5 
      5zm0-8c-1.654 
      0-3 1.346-3 3s1.346 3 
      3 3 3-1.346 3-3-1.346-3-3-3z"
                                    />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </div>
                          <div className="edit-field">
                            <label>Activo:</label>
                            <input
                              type="checkbox"
                              checked={!!editingSubUserData.activo}
                              onChange={(e) =>
                                handleChangeSubUserField(
                                  "activo",
                                  e.target.checked
                                )
                              }
                            />
                          </div>

                          <div className="edit-field">
                            <label>Recibir Correos:</label>
                            <input
                              type="checkbox"
                              checked={!!editingSubUserData.recibircorreos}
                              onChange={(e) =>
                                handleChangeSubUserField(
                                  "recibircorreos",
                                  e.target.checked
                                )
                              }
                            />
                          </div>

                          <div className="edit-field">
                            <label>Email Notificaciones:</label>
                            <input
                              type="text"
                              value={
                                editingSubUserData.emailnotificaciones || ""
                              }
                              onChange={(e) =>
                                handleChangeSubUserField(
                                  "emailnotificaciones",
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </div>

                        <div className="user-actions">
                          <button
                            onClick={handleSaveSubUser}
                            className="btn btn-primary"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={handleCancelSubUserCreateOrEdit}
                            className="btn btn-secondary"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {usuariosData.length > 0 && (
        <div className="pagination-concursos">
          <button
            onClick={() =>
              setSearchParams({
                ...Object.fromEntries(searchParams),
                page: `${Math.max(page - 1, 1)}`,
              })
            }
            disabled={page === 1}
            className="pagination-btn"
          >
            Anterior
          </button>
          <span className="pagination-info">
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() =>
              setSearchParams({
                ...Object.fromEntries(searchParams),
                page: `${Math.min(page + 1, totalPages)}`,
              })
            }
            disabled={page === totalPages || totalPages === 0}
            className="pagination-btn"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

export default Usuarios;
